import { ProfileCard } from '@/features/unifiedProfileCard'
import { useStyles } from './styles'
import { $steamId, $unifiedGigi, $userLanguage, $userSettings, $userToken } from '@/entities/user'
import { $steamUserData } from '@/entities/platforms/steam/store'
import { lang } from '@/shared/constants/lang'
import { BackgroundShading } from '@/shared/ui/backgroundShading'
import { useUnit } from 'effector-react'
import { Statistics } from '@/shared/ui/dataWrappers/farcanaStatistics'
import useFormatUnifiedStats from './hooks/useFormatUnifiedStats'
import { FormattedStat } from '@/shared/constants/types'
import { GameType } from '@/entities/platforms/steam/types/types'
import { AppSwitch } from '@/shared/ui/appSwitch'
import { useMemo, useRef } from 'react'
import { insertUserSettingsFx } from '@/entities/user'
import { SettingsEvents } from '@/entities/user'
import { changeLocalPrivacy, getUserProfileFx } from '@/entities/user'
import { debounce } from '@/shared/util/debounce'
import RecommendedButton from '@/features/recommendedButton'
import { goToRecommendations } from '@/app/routing/events'
import { GamesList } from '@/features/profileTabs/gameList'
import { ExpensiveItemsList } from '@/features/expensiveItemsList'

export const UnifiedProfileView = () => {
  const { classes } = useStyles()
  const userLang = useUnit($userLanguage)
  const userToken = useUnit($userToken)
  const userSettings = useUnit($userSettings)
  const gamesList = useUnit($steamUserData)?.gamesList
  const itemsList = useUnit($steamUserData)?.itemsList ?? null
  const texts = lang[userLang].pages.profile
  const gigi = useUnit($unifiedGigi)
  const storeIsPrivate = useMemo(() => !!userSettings?.is_private, [userSettings])
  const stats: FormattedStat[] = useFormatUnifiedStats()
  const steamId = useUnit($steamId)
  const favoriteGames = useMemo<GameType[] | null>(() => {
    if (!gamesList?.length) return null
    const g = [...gamesList].sort((a, b) => b.total_hours - a.total_hours).slice(0, 5)
    return g
  }, [gamesList])

  const debouncedSendPrivacyUpdate = useRef(
    debounce((newVal: boolean) => {
      if (!userToken) return
      insertUserSettingsFx({
        token: userToken,
        data: {
          key: SettingsEvents.PRIVATE_ACCOUNT,
          value: newVal,
        },
      })
      getUserProfileFx(userToken)
    }, 1000)
  ).current

  function handleChangePrivacy() {
    changeLocalPrivacy(!storeIsPrivate)
    debouncedSendPrivacyUpdate(!storeIsPrivate)
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <img src="/assets/subscriptionRelated/gamedLogo.webp" className={classes.logoImg} />
        <img src="/assets/backgrounds/unifiedprofilebg.webp" alt="" className={classes.bgImg} />
        <BackgroundShading bottom="0" height="112px" />
      </div>

      <div className={classes.content}>
        <div style={{ padding: '0px 16px' }}>
          <ProfileCard variant="profile" balance={gigi} />
          <h1 className={classes.title}>{texts.gamingLegacy}</h1>
        </div>

        <div className={classes.stats}>
          {stats.map((item, index) => (
            <Statistics key={index} value={item.value} name={item.name} unit={item.unit} />
          ))}
        </div>
        {itemsList && itemsList.length > 0 && (
          <>
            <h1 className={classes.title} style={{ padding: '0px 16px', paddingTop: '12px' }}>
              {texts.expensiveItems}
            </h1>
            <ExpensiveItemsList itemsList={itemsList} />
          </>
        )}
        <div className={classes.privacy}>
          <h1>{texts.private}</h1>
          <AppSwitch handleSwitch={handleChangePrivacy} checked={storeIsPrivate} />
        </div>
        <br />
        {steamId && (
          <div style={{ padding: '0px 16px' }}>
            <RecommendedButton handleClick={goToRecommendations} />
          </div>
        )}
        <div style={{ padding: '0px 16px' }}>
          {favoriteGames && (
            <>
              <h1 className={classes.title}>{texts.favoriteGames}</h1>
              <GamesList gamesList={favoriteGames} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
